import { useState, useEffect, useCallback, useMemo } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNIT_TYPES = [
  { id: "studio", label: "Studio" },
  { id: "1br", label: "1 BR" },
  { id: "2br", label: "2 BR" },
  { id: "3br", label: "3 BR" },
  { id: "4br", label: "4 BR+" },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmt2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─── Styling primitives (kept inline to match existing tab conventions) ───
const cellInputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#fff",
  padding: "0.45rem 0.55rem",
  borderRadius: 5,
  fontSize: "0.85rem",
};
const labelStyle = {
  display: "block",
  color: "#7E92A8",
  fontSize: "0.66rem",
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  marginBottom: "0.25rem",
};
const cardStyle = {
  background: "rgba(255,255,255,0.025)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 10,
  padding: "1.1rem 1.3rem",
};

const FounderDrawTab = () => {
  // ─── Pricing config from sourced-products endpoint ───
  const [caps, setCaps] = useState({});
  const [labor, setLabor] = useState({});
  const [rentMarkup, setRentMarkup] = useState(1.20);
  const [portfolioMarkup, setPortfolioMarkup] = useState(1.15);

  // ─── Founder draw config (persisted) ───
  const [avgRents, setAvgRents] = useState({});
  const [overhead, setOverhead] = useState(800);
  const [workingCapPct, setWorkingCapPct] = useState(15);
  const [taxReservePct, setTaxReservePct] = useState(28);
  const [ceoSplit, setCeoSplit] = useState(50);
  const [ceoLabel, setCeoLabel] = useState("Shannon (CEO)");
  const [cooLabel, setCooLabel] = useState("Shaneka (COO)");

  // ─── Pipeline (this month's contracts — local state, not persisted) ───
  const [pipeline, setPipeline] = useState({
    studio: { std: 0, port: 0 },
    "1br": { std: 0, port: 0 },
    "2br": { std: 2, port: 0 },
    "3br": { std: 1, port: 0 },
    "4br": { std: 0, port: 0 },
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pricing, draw] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/sourced-products`, { credentials: "include" }).then(r => r.ok ? r.json() : {}),
        fetch(`${BACKEND_URL}/api/admin/founder-draw/config`, { credentials: "include" }).then(r => r.ok ? r.json() : {}),
      ]);
      setCaps(pricing.material_caps || {});
      setLabor(pricing.labor_fees || {});
      setRentMarkup(pricing.rent_markup || 1.20);
      setPortfolioMarkup(pricing.portfolio_markup || 1.15);

      setAvgRents(draw.avg_rents || {});
      setOverhead(draw.monthly_overhead ?? 800);
      setWorkingCapPct(draw.working_capital_pct ?? 15);
      setTaxReservePct(draw.tax_reserve_pct ?? 28);
      setCeoSplit(draw.ceo_split_pct ?? 50);
      if (draw.ceo_label) setCeoLabel(draw.ceo_label);
      if (draw.coo_label) setCooLabel(draw.coo_label);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/founder-draw/config`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          avg_rents: avgRents,
          monthly_overhead: parseFloat(overhead) || 0,
          working_capital_pct: parseFloat(workingCapPct) || 0,
          tax_reserve_pct: parseFloat(taxReservePct) || 0,
          ceo_split_pct: parseFloat(ceoSplit) || 0,
          ceo_label: ceoLabel,
          coo_label: cooLabel,
        }),
      });
      if (r.ok) {
        setSavedAt(new Date().toLocaleTimeString());
      }
    } catch {}
    setSaving(false);
  };

  const updatePipeline = (unit, kind, val) => {
    const n = Math.max(0, parseInt(val) || 0);
    setPipeline(prev => ({ ...prev, [unit]: { ...prev[unit], [kind]: n } }));
  };

  const updateRent = (unit, val) => {
    const n = Math.max(0, parseFloat(val) || 0);
    setAvgRents(prev => ({ ...prev, [unit]: n }));
  };

  // ─── What-If Scenarios — preset pipeline mixes ───
  const scenarios = useMemo(() => ([
    { id: "reset", label: "Reset", note: "Zero out all units",
      fill: { studio: { std: 0, port: 0 }, "1br": { std: 0, port: 0 }, "2br": { std: 0, port: 0 }, "3br": { std: 0, port: 0 }, "4br": { std: 0, port: 0 } } },
    { id: "slow", label: "Slow Month", note: "3 mixed units, all standard",
      fill: { studio: { std: 0, port: 0 }, "1br": { std: 1, port: 0 }, "2br": { std: 2, port: 0 }, "3br": { std: 0, port: 0 }, "4br": { std: 0, port: 0 } } },
    { id: "steady", label: "Steady Month", note: "6 units, light portfolio",
      fill: { studio: { std: 1, port: 0 }, "1br": { std: 2, port: 0 }, "2br": { std: 2, port: 1 }, "3br": { std: 0, port: 0 }, "4br": { std: 0, port: 0 } } },
    { id: "volume", label: "Volume Month", note: "15 mixed, half portfolio",
      fill: { studio: { std: 1, port: 1 }, "1br": { std: 2, port: 3 }, "2br": { std: 2, port: 4 }, "3br": { std: 1, port: 1 }, "4br": { std: 0, port: 0 } } },
    { id: "reit", label: "REIT Pitch Win", note: "10-unit 2BR portfolio (Greystar/MAA)",
      fill: { studio: { std: 0, port: 0 }, "1br": { std: 0, port: 0 }, "2br": { std: 0, port: 10 }, "3br": { std: 0, port: 0 }, "4br": { std: 0, port: 0 } } },
    { id: "dha", label: "DHA Voucher Run", note: "8-unit voucher portfolio mix",
      fill: { studio: { std: 0, port: 2 }, "1br": { std: 0, port: 3 }, "2br": { std: 0, port: 2 }, "3br": { std: 0, port: 1 }, "4br": { std: 0, port: 0 } } },
    { id: "founders", label: "Founders Pay Day", note: "20-unit institutional rollout",
      fill: { studio: { std: 0, port: 2 }, "1br": { std: 0, port: 5 }, "2br": { std: 0, port: 8 }, "3br": { std: 0, port: 4 }, "4br": { std: 0, port: 1 } } },
  ]), []);

  const applyScenario = (s) => setPipeline(s.fill);

  // ═══ THE MATH (client-side, reactive) ═══
  const lineItems = useMemo(() => {
    return UNIT_TYPES.map(u => {
      const rent = avgRents[u.id] || 0;
      const cap = caps[u.id] || 0;
      const lab = labor[u.id] || 0;
      const stdFee = rent * rentMarkup;
      const portFee = rent * portfolioMarkup;
      const stdMargin = stdFee - cap - lab;
      const portMargin = portFee - cap - lab;
      const stdCount = pipeline[u.id]?.std || 0;
      const portCount = pipeline[u.id]?.port || 0;
      const totalUnits = stdCount + portCount;
      const lineRevenue = stdFee * stdCount + portFee * portCount;
      const lineMaterials = cap * totalUnits;
      const lineLabor = lab * totalUnits;
      const lineGross = lineRevenue - lineMaterials - lineLabor;
      return {
        ...u,
        rent, cap, lab,
        stdFee, portFee, stdMargin, portMargin,
        stdCount, portCount, totalUnits,
        lineRevenue, lineMaterials, lineLabor, lineGross,
      };
    });
  }, [avgRents, caps, labor, rentMarkup, portfolioMarkup, pipeline]);

  const totals = useMemo(() => {
    const t = lineItems.reduce((acc, l) => ({
      units: acc.units + l.totalUnits,
      revenue: acc.revenue + l.lineRevenue,
      materials: acc.materials + l.lineMaterials,
      crew: acc.crew + l.lineLabor,
      grossProfit: acc.grossProfit + l.lineGross,
    }), { units: 0, revenue: 0, materials: 0, crew: 0, grossProfit: 0 });

    const overheadVal = parseFloat(overhead) || 0;
    const opIncome = t.grossProfit - overheadVal;
    const workingCapHold = Math.max(0, opIncome) * (parseFloat(workingCapPct) || 0) / 100;
    const taxableBase = Math.max(0, opIncome - workingCapHold);
    const taxReserve = taxableBase * (parseFloat(taxReservePct) || 0) / 100;
    const founderPool = taxableBase - taxReserve;
    const ceoPct = Math.max(0, Math.min(100, parseFloat(ceoSplit) || 0));
    const cooPct = 100 - ceoPct;
    const ceoTakeHome = founderPool * ceoPct / 100;
    const cooTakeHome = founderPool * cooPct / 100;

    return {
      ...t,
      overheadVal,
      opIncome,
      workingCapHold,
      taxableBase,
      taxReserve,
      founderPool,
      ceoPct, cooPct,
      ceoTakeHome, cooTakeHome,
    };
  }, [lineItems, overhead, workingCapPct, taxReservePct, ceoSplit]);

  if (loading) {
    return <div data-testid="founder-draw-tab" style={{ padding: "2rem", color: "#7E92A8", textAlign: "center" }}>Loading the math…</div>;
  }

  return (
    <div data-testid="founder-draw-tab" style={{ padding: "0 0.5rem" }}>
      {/* ─── Header ─── */}
      <div style={{ marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.85rem" }}>
        <div>
          <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
            Founder Draw Calculator
          </h1>
          <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.5, maxWidth: 760 }}>
            Plan your month: enter contracts you&apos;re lining up by unit type, and see — in real time — what each of you can actually pay yourselves after materials, crew, overhead, working capital reserve, and taxes. <strong style={{ color: "#FFB347" }}>Internal-only. Pulls live from your sourced-products caps + markups.</strong>
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {savedAt && <span style={{ color: "#00DC78", fontSize: "0.74rem" }}>✓ Saved {savedAt}</span>}
          <button onClick={saveSettings} disabled={saving} data-testid="btn-save-settings" style={{ padding: "0.6rem 1.15rem", background: saving ? "rgba(255,255,255,0.10)" : "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.82rem", cursor: saving ? "wait" : "pointer" }}>
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </div>

      {/* ─── WHAT-IF SCENARIOS ─── */}
      <div data-testid="what-if-row" style={{ marginBottom: "1rem" }}>
        <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.55rem" }}>What-if Scenarios</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.45rem" }}>
          {scenarios.map(s => (
            <button
              key={s.id}
              onClick={() => applyScenario(s)}
              data-testid={`scenario-${s.id}`}
              title={s.note}
              style={{
                padding: "0.55rem 1rem",
                background: s.id === "reset" ? "transparent" : "rgba(255,255,255,0.04)",
                border: `1px solid ${s.id === "reset" ? "rgba(255,139,107,0.30)" : "rgba(0,255,255,0.20)"}`,
                color: s.id === "reset" ? "#FF8B6B" : "#5AC8D8",
                borderRadius: 5,
                fontSize: "0.78rem",
                fontWeight: 700,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── PIPELINE TABLE ─── */}
      <div style={{ ...cardStyle, marginBottom: "1.1rem", padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "0.95rem 1.3rem", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(0,255,255,0.03)" }}>
          <h2 style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, margin: 0 }}>This Month&apos;s Pipeline</h2>
          <div style={{ color: "#7E92A8", fontSize: "0.78rem", marginTop: "0.2rem" }}>
            Set the contracts you expect to deliver. Standard = Rent × {rentMarkup.toFixed(2)} · Portfolio (10+) = Rent × {portfolioMarkup.toFixed(2)}.
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="pipeline-table" style={{ width: "100%", minWidth: 880, borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.025)" }}>
                {["Unit", "Avg Rent", "# Std", "# Portfolio", "Std Fee", "Port Fee", "Mat Cap", "Crew", "Per-Unit Margin", "Line Revenue", "Line Profit"].map(h => (
                  <th key={h} style={{ padding: "0.65rem 0.75rem", textAlign: h === "Unit" ? "left" : "right", color: "#7E92A8", fontWeight: 700, fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lineItems.map(l => (
                <tr key={l.id} data-testid={`pipeline-row-${l.id}`} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <td style={{ padding: "0.55rem 0.75rem", color: "#fff", fontWeight: 700 }}>{l.label}</td>
                  <td style={{ padding: "0.4rem 0.5rem" }}>
                    <input type="number" value={l.rent} onChange={e => updateRent(l.id, e.target.value)} data-testid={`rent-${l.id}`} style={{ ...cellInputStyle, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "0.4rem 0.5rem" }}>
                    <input type="number" value={l.stdCount} onChange={e => updatePipeline(l.id, "std", e.target.value)} data-testid={`std-count-${l.id}`} style={{ ...cellInputStyle, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "0.4rem 0.5rem" }}>
                    <input type="number" value={l.portCount} onChange={e => updatePipeline(l.id, "port", e.target.value)} data-testid={`port-count-${l.id}`} style={{ ...cellInputStyle, textAlign: "right" }} />
                  </td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: "#5AC8D8", fontWeight: 700 }}>{fmt(l.stdFee)}</td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: "#5AC8D8", fontWeight: 700 }}>{fmt(l.portFee)}</td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: "#ADBED1" }}>{fmt(l.cap)}</td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: "#ADBED1" }}>{fmt(l.lab)}</td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: l.stdMargin >= 0 ? "#00DC78" : "#FF8B6B", fontWeight: 800 }}>{fmt(l.stdMargin)}</td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: "#fff", fontWeight: 700 }}>{fmt(l.lineRevenue)}</td>
                  <td style={{ padding: "0.55rem 0.75rem", textAlign: "right", color: l.lineGross >= 0 ? "#00DC78" : "#FF8B6B", fontWeight: 800 }}>{fmt(l.lineGross)}</td>
                </tr>
              ))}
              <tr style={{ background: "rgba(0,255,255,0.04)", borderTop: "2px solid rgba(0,255,255,0.20)" }}>
                <td colSpan={2} style={{ padding: "0.7rem 0.75rem", color: "#fff", fontWeight: 800 }}>TOTALS — {totals.units} units</td>
                <td colSpan={6} />
                <td data-testid="total-revenue" style={{ padding: "0.7rem 0.75rem", textAlign: "right", color: "#00FFFF", fontWeight: 800 }}>{fmt(totals.revenue)}</td>
                <td data-testid="total-gross-profit" style={{ padding: "0.7rem 0.75rem", textAlign: "right", color: totals.grossProfit >= 0 ? "#00DC78" : "#FF8B6B", fontWeight: 800 }}>{fmt(totals.grossProfit)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ─── DEDUCTIONS + SPLIT (3-col grid) ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1rem", marginBottom: "1.1rem" }}>
        <div style={cardStyle}>
          <div style={{ color: "#FFB347", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.7rem" }}>Operating Overhead</div>
          <label style={labelStyle}>Monthly fixed costs ($)</label>
          <input type="number" value={overhead} onChange={e => setOverhead(e.target.value)} data-testid="input-overhead" style={cellInputStyle} />
          <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.45rem", fontStyle: "italic" }}>Software, hosting, insurance, fuel, phone, Resend, Stripe fees, etc.</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#FFB347", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.7rem" }}>Working Capital Reserve</div>
          <label style={labelStyle}>Hold-back % (next jobs)</label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="range" min="0" max="50" value={workingCapPct} onChange={e => setWorkingCapPct(e.target.value)} data-testid="slider-working-cap" style={{ flex: 1 }} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.95rem", minWidth: 44, textAlign: "right" }}>{workingCapPct}%</span>
          </div>
          <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.45rem", fontStyle: "italic" }}>Cash kept in the business to mobilize the next contract before client pays.</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#FFB347", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.7rem" }}>Tax Reserve</div>
          <label style={labelStyle}>Federal SE + TX franchise %</label>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input type="range" min="0" max="45" value={taxReservePct} onChange={e => setTaxReservePct(e.target.value)} data-testid="slider-tax" style={{ flex: 1 }} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.95rem", minWidth: 44, textAlign: "right" }}>{taxReservePct}%</span>
          </div>
          <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.45rem", fontStyle: "italic" }}>Estimate. Confirm with your CPA each quarter — Texas franchise is ~0.375% of revenue above $1.23M; SE + federal income roughly 25–30% of net.</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#FFB347", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.7rem" }}>Owner Split</div>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <input value={ceoLabel} onChange={e => setCeoLabel(e.target.value)} placeholder="CEO label" data-testid="input-ceo-label" style={{ ...cellInputStyle, flex: 1 }} />
            <input value={cooLabel} onChange={e => setCooLabel(e.target.value)} placeholder="COO label" data-testid="input-coo-label" style={{ ...cellInputStyle, flex: 1 }} />
          </div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <span style={{ color: "#5AC8D8", fontSize: "0.78rem", minWidth: 100 }}>{ceoLabel}</span>
            <input type="range" min="0" max="100" value={ceoSplit} onChange={e => setCeoSplit(e.target.value)} data-testid="slider-ceo-split" style={{ flex: 1 }} />
            <span style={{ color: "#fff", fontWeight: 800, fontSize: "0.95rem", minWidth: 44, textAlign: "right" }}>{ceoSplit}%</span>
          </div>
          <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.4rem", fontStyle: "italic" }}>{cooLabel} receives {100 - ceoSplit}%.</div>
        </div>
      </div>

      {/* ─── WATERFALL: Revenue → Take-Home ─── */}
      <div style={{ ...cardStyle, marginBottom: "1.1rem" }}>
        <div style={{ color: "#7E92A8", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.85rem" }}>Monthly Waterfall</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.55rem" }}>
          <WaterfallStep label="Gross Revenue" value={totals.revenue} testId="wf-revenue" tone="positive" />
          <WaterfallStep label="− Materials" value={-totals.materials} testId="wf-materials" tone="neutral" />
          <WaterfallStep label="− Crew Labor" value={-totals.crew} testId="wf-crew" tone="neutral" />
          <WaterfallStep label="= Gross Profit" value={totals.grossProfit} testId="wf-gross" tone={totals.grossProfit >= 0 ? "positive" : "negative"} />
          <WaterfallStep label="− Overhead" value={-totals.overheadVal} testId="wf-overhead" tone="neutral" />
          <WaterfallStep label="= Operating Income" value={totals.opIncome} testId="wf-op-income" tone={totals.opIncome >= 0 ? "positive" : "negative"} />
          <WaterfallStep label={`− Working Cap (${workingCapPct}%)`} value={-totals.workingCapHold} testId="wf-wc" tone="neutral" />
          <WaterfallStep label={`− Tax Reserve (${taxReservePct}%)`} value={-totals.taxReserve} testId="wf-tax" tone="neutral" />
          <WaterfallStep label="= Founder Pool" value={totals.founderPool} testId="wf-pool" tone={totals.founderPool >= 0 ? "positive" : "negative"} highlight />
        </div>
      </div>

      {/* ─── TAKE-HOME CARDS ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem", marginBottom: "1.1rem" }}>
        <TakeHomeCard label={ceoLabel} pct={totals.ceoPct} monthly={totals.ceoTakeHome} testId="takehome-ceo" />
        <TakeHomeCard label={cooLabel} pct={totals.cooPct} monthly={totals.cooTakeHome} testId="takehome-coo" />
      </div>

      {/* ─── REALITY CHECK STRIP ─── */}
      <div data-testid="reality-check" style={{ background: totals.founderPool < 0 ? "rgba(255,139,107,0.08)" : "rgba(0,220,120,0.06)", border: `1px solid ${totals.founderPool < 0 ? "rgba(255,139,107,0.3)" : "rgba(0,220,120,0.25)"}`, borderRadius: 10, padding: "1rem 1.3rem", marginBottom: "1.1rem" }}>
        <div style={{ color: totals.founderPool < 0 ? "#FF8B6B" : "#00DC78", fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "0.45rem" }}>
          Reality Check
        </div>
        <div style={{ color: "#fff", fontSize: "0.92rem", lineHeight: 1.55 }}>
          {totals.founderPool < 0 ? (
            <>This month&apos;s pipeline is <strong style={{ color: "#FF8B6B" }}>{fmt(Math.abs(totals.founderPool))} short</strong> of paying you both. Either add more units, push for portfolio (10+ unit) contracts at the {portfolioMarkup.toFixed(2)}× rate, or trim overhead. The math is honest — fix the inputs, not the formula.</>
          ) : totals.units === 0 ? (
            <>Zero units in the pipeline. Add expected contracts above to see what each of you takes home this month. Even one 2BR portfolio contract moves the needle.</>
          ) : (
            <>If this pipeline closes as planned, <strong style={{ color: "#00DC78" }}>{fmt(totals.founderPool)}/month</strong> goes to you both — <strong style={{ color: "#00FFFF" }}>{fmt(totals.founderPool * 12)}/year</strong> annualized. Working capital reserve: {fmt(totals.workingCapHold)}/mo. Tax money set aside: {fmt(totals.taxReserve)}/mo (don&apos;t spend it). 💛</>
          )}
        </div>
      </div>

      {/* ─── DISCLAIMER ─── */}
      <div style={{ color: "#7E92A8", fontSize: "0.72rem", fontStyle: "italic", lineHeight: 1.55, padding: "0 0.5rem 1rem" }}>
        Calculator uses your live caps + labor + markup from the Sourced Products tab. Tax estimate is a planning blanket — confirm with your CPA. Working capital is held inside the business to fund the next contract&apos;s materials before client payment. Annualized = monthly × 12 (does not assume seasonal variance). Built different. Built honest.
      </div>
    </div>
  );
};

// ─── Sub-components ───
const WaterfallStep = ({ label, value, testId, tone = "neutral", highlight = false }) => {
  const colors = {
    positive: "#00DC78",
    negative: "#FF8B6B",
    neutral: "#ADBED1",
  };
  return (
    <div data-testid={testId} style={{
      background: highlight ? "rgba(0,255,255,0.08)" : "rgba(255,255,255,0.025)",
      border: highlight ? "1px solid rgba(0,255,255,0.35)" : "1px solid rgba(255,255,255,0.06)",
      borderRadius: 8,
      padding: "0.7rem 0.85rem",
    }}>
      <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "0.3rem" }}>{label}</div>
      <div style={{ color: highlight ? "#00FFFF" : colors[tone], fontSize: highlight ? "1.15rem" : "1rem", fontWeight: 800 }}>{fmt(value)}</div>
    </div>
  );
};

const TakeHomeCard = ({ label, pct, monthly, testId }) => {
  const positive = monthly >= 0;
  return (
    <div data-testid={testId} style={{
      background: positive ? "linear-gradient(135deg, rgba(0,220,120,0.10) 0%, rgba(0,255,255,0.06) 100%)" : "rgba(255,139,107,0.06)",
      border: `1px solid ${positive ? "rgba(0,220,120,0.30)" : "rgba(255,139,107,0.25)"}`,
      borderRadius: 12,
      padding: "1.4rem 1.5rem",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.6rem" }}>
        <div style={{ color: "#fff", fontSize: "1rem", fontWeight: 800, letterSpacing: "-0.01em" }}>{label}</div>
        <div style={{ color: "#7E92A8", fontSize: "0.78rem", fontWeight: 700 }}>{pct.toFixed(0)}% of pool</div>
      </div>
      <div style={{ color: positive ? "#00FFFF" : "#FF8B6B", fontSize: "2rem", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{fmt2(monthly)}</div>
      <div style={{ color: "#ADBED1", fontSize: "0.82rem", marginTop: "0.35rem" }}>per month · <strong style={{ color: "#fff" }}>{fmt(monthly * 12)}</strong> annualized</div>
    </div>
  );
};

export default FounderDrawTab;
